import {useContext, useState, MutableRefObject, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {PlaylistUser} from '../../../../../../../backend/responses/playlist-user';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {AppLanguageTag} from '../../../../../../../i18n/types';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';
import i18n from 'i18next';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../../components/search-input/search-input';
import {Box} from '@mui/material';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Type = 'type',
  State = 'state',
}

export default function PlaylistUsersProfessionalCaregiverOfficeScreen({
  canWrite,
}: {
  canWrite: boolean;
}) {
  // Get params
  let {playlistId, entityId}: {playlistId?: string; entityId?: string} = useParams();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [updatePlaylistUserDialogOpened, setUpdatePlaylistUserDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {userId: string; userName: string; playlistActiveFlag: boolean} | undefined
  > = useRef();

  // Translations
  const {t} = useTranslation();

  // Fetch users
  const handleGetUsers = async (limit: number, skip: number, search?: string) => {
    // Check ids
    if (!playlistId || !entityId) {
      // Error
      throw new Error('Empty entity or playlist id.');
    }

    // Load users
    const result: {totalCount: number; data: Array<PlaylistUser>} =
      await PlaylistsRepository.instance.getPlaylistUsersInEntityAsAffiliatedForEntity(
        entityId,
        playlistId,
        skip,
        limit,
        search
      );

    // Process data
    const processedData: PaginatedTableData<PlaylistUser> = result.data.map((obj) => {
      // Link
      let toLink: string = '';
      if (obj.user.type === UserType.Senior) {
        toLink = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${obj.user.id}`;
      } else if (obj.user.type === UserType.Caregiver) {
        toLink = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}/${obj.user.id}${ScreenPaths.Senior_Profile}`;
      }

      // Return row values
      return {
        id: obj.user.id || '',
        item: obj,
        avatarUrl: obj.user.picture,
        enabled: obj.isActive === true ? true : false,
        toLink: toLink,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.user.name || '',
          },
          {
            columnId: ColumnIdentifier.Type,
            value: obj.user.type ? t(translations.user_types[obj.user.type]) : '',
          },
          {
            columnId: ColumnIdentifier.State,
            value:
              obj.isActive === true
                ? t(translations.playlists.professional_caregiver.edit.users.state_value_true)
                : t(translations.playlists.professional_caregiver.edit.users.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<PlaylistUser>({
    fetchData: handleGetUsers,
  });

  const handleAskUpdatePlaylistUserState = (userId: string, value: boolean) => {
    // Get user name
    const userName: string | undefined = data
      .find((obj) => obj.id === userId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set user info for later
    dataToUpdateRef.current = {
      userId: userId,
      userName: userName || '',
      playlistActiveFlag: value,
    };

    // Open modal
    setUpdatePlaylistUserDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdatePlaylistUserState = () => {
    // Close modal
    setUpdatePlaylistUserDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdatePlaylistUserState = () => {
    // Close modal
    setUpdatePlaylistUserDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdatePlaylistUserState();
  };

  const handleUpdatePlaylistUserState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const userId = dataToUpdateRef.current.userId;
      const value = dataToUpdateRef.current.playlistActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === userId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        userId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.playlists.professional_caregiver.edit.users.state_value_true)
              : t(translations.playlists.professional_caregiver.edit.users.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdatePlaylistUserState(userId, playlistId || '', value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.playlists.professional_caregiver.edit.users.errors
            .update_state_playlist_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdatePlaylistUserState = async (
    userId: string,
    playlistId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistActiveStateForUser(
        userId,
        playlistId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === userId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        userId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.playlists.professional_caregiver.edit.users.state_value_true)
              : t(translations.playlists.professional_caregiver.edit.users.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(
              translations.playlists.professional_caregiver.edit.users.errors
                .enable_playlist_error_message
            )
          : t(
              translations.playlists.professional_caregiver.edit.users.errors
                .disable_playlist_error_message
            ),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist with id ${playlistId} to ${value} for user with id ${userId}.`,
        error
      );
    }
  };

  // Get playlist name
  let playlistNameLocalized: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.ptPT ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.esES:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.esES ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.glES:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.glES ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.enUS:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.enUS ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    case AppLanguageTag.ptBR:
      playlistNameLocalized =
        officeContext.state.playlistForm?.nameLocalized?.ptBR ||
        officeContext.state.playlistForm?.name ||
        '';
      break;
    default:
      playlistNameLocalized = officeContext.state.playlistForm?.name || '';
      break;
  }

  const baseUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`;

  return (
    <>
      <GenericHeader
        isLoading={!officeContext.state.playlistForm}
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: baseUrl,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.playlists),
            link: `${baseUrl}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}`,
          },
          {
            label: officeContext.state.playlistForm?.name || '',
            link: `${baseUrl}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}/${playlistId}`,
          },
          {
            label: t(translations.playlists.professional_caregiver.edit.tabs.users),
            link: baseUrl,
          },
        ]}
        leftColumn={
          <>
            <SearchInput
              placeholder={t(
                translations.playlists.professional_caregiver.root.search_input_placeholder
              )}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </>
        }
        rightColumn={null}
      />

      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'user',
            }}
            emptyTableMessage={t(translations.playlists.professional_caregiver.edit.users.no_users)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.playlists.professional_caregiver.edit.users.name_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.Type,
                title: t(translations.playlists.professional_caregiver.edit.users.type_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.playlists.professional_caregiver.edit.users.state_header),
                flexGrow: 1,
                button: {
                  type: 'toggle',
                  showColumnValue: true,
                  toggle: {
                    enabledValue: t(
                      translations.playlists.professional_caregiver.edit.users.state_value_true
                    ),
                    handleClick: handleAskUpdatePlaylistUserState,
                  },
                  disabled: !canWrite,
                  disabledTooltip: t(translations.user_playlist_items.state_disabled_tooltip),
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={false}
          />
        </Box>
      </Box>
      <AlertModal
        id="confirm-disable-playlist-user-state"
        opened={updatePlaylistUserDialogOpened.disableDialogOpened}
        title={t(
          translations.playlists.professional_caregiver.edit.users.disable_playlist_dialog_title,
          {
            name: playlistNameLocalized,
          }
        )}
        content={t(
          translations.playlists.professional_caregiver.edit.users.disable_playlist_dialog_message,
          {
            name: dataToUpdateRef.current?.userName || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistUserState}
        actions={[
          {
            label: t(
              translations.playlists.professional_caregiver.edit.users
                .disable_playlist_dialog_action_yes_button
            ),
            type: 'destructive',
            handleClick: handleConfirmUpdatePlaylistUserState,
          },
          {
            label: t(
              translations.playlists.professional_caregiver.edit.users
                .disable_playlist_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistUserState,
          },
        ]}
      />
      <AlertModal
        id="confirm-enable-playlist-user-state"
        opened={updatePlaylistUserDialogOpened.enableDialogOpened}
        title={t(
          translations.playlists.professional_caregiver.edit.users.enable_playlist_dialog_title,
          {
            name: playlistNameLocalized,
          }
        )}
        content={t(
          translations.playlists.professional_caregiver.edit.users.enable_playlist_dialog_message,
          {
            name: dataToUpdateRef.current?.userName || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistUserState}
        actions={[
          {
            label: t(
              translations.playlists.professional_caregiver.edit.users
                .enable_playlist_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistUserState,
          },
          {
            label: t(
              translations.playlists.professional_caregiver.edit.users
                .enable_playlist_dialog_action_yes_button
            ),
            type: 'nondestructive',
            handleClick: handleConfirmUpdatePlaylistUserState,
          },
        ]}
      />
    </>
  );
}
